/**
 * Accessor for /listActionItems + /publishActionItem. Mirrors
 * MetricCommentsApi: `actor` carries the caller's alias on writes (the
 * backend derives the stored author from its own identity). Cache policy
 * lives in the RTK endpoint, not here.
 */

import {
  ErrorCode,
  ReportingApiClient,
  ReportingApiError,
} from 'src/accessors/ReportingApiClient';
import { WBR_REVIEW_TYPE } from 'src/constants';
import { currentUserAlias } from 'src/accessors/currentUser';
import {
  ActionItemDraft,
  ActionItemRecord,
  ActionItemThreadAnchor,
  ListActionItemsInput,
  ListActionItemsOutput,
  PublishActionItemInput,
  PublishActionItemOutput,
} from 'src/types/actionItems.types';

/** Backend page cap is 100; the active set target is ~100, so scans stay short. */
const PAGE_SIZE = 100;

/**
 * Fail-loud pagination bound. Unlike the per-week callouts scan, the dashboard
 * list is all-time (DONE/CANCELLED items stay active), so give it years of
 * headroom; hitting the cap should mean a bug, and flags that closed items
 * need server-side archiving or filtering.
 */
const MAX_PAGES = 50;

class ActionItemsApiClient extends ReportingApiClient {
  private publish(
    input: PublishActionItemInput,
  ): Promise<PublishActionItemOutput> {
    return this.post<PublishActionItemOutput>('/publishActionItem', input);
  }

  private list(input: ListActionItemsInput): Promise<ListActionItemsOutput> {
    return this.post<ListActionItemsOutput>('/listActionItems', input);
  }

  /** Every record for a scope, following paginationKey (bounded, fail-loud). */
  private async fetchAll(
    input: Omit<ListActionItemsInput, 'pageSize' | 'paginationKey'>,
  ): Promise<ActionItemRecord[]> {
    const all: ActionItemRecord[] = [];
    let paginationKey: string | undefined;
    for (let pageNo = 0; pageNo < MAX_PAGES; pageNo++) {
      const page = await this.list({
        ...input,
        pageSize: PAGE_SIZE,
        paginationKey,
      });
      all.push(...(page.actionItems ?? []));
      paginationKey = page.paginationKey || undefined;
      if (!paginationKey) {
        return all;
      }
    }
    throw new ReportingApiError(
      500,
      ErrorCode.InternalFailure,
      `listActionItems pagination did not terminate within ${MAX_PAGES} pages`,
    );
  }

  /** All active action items for the dashboard review (both types). */
  public listDashboard(dashboardId: string): Promise<ActionItemRecord[]> {
    return this.fetchAll({
      dashboardId,
      reviewType: WBR_REVIEW_TYPE,
      scope: 'BY_DASHBOARD',
    });
  }

  /** One callout thread's action items. */
  public listThread(
    anchor: ActionItemThreadAnchor,
  ): Promise<ActionItemRecord[]> {
    return this.fetchAll({
      dashboardId: anchor.dashboardId,
      reviewType: WBR_REVIEW_TYPE,
      scope: 'BY_CALLOUT',
      reportingPeriod: anchor.reportingPeriod,
      metricKey: anchor.metricKey,
      metricCategory: anchor.metricCategory,
    });
  }

  /** Creates a thread-anchored action item. */
  public async createForThread(
    anchor: ActionItemThreadAnchor,
    draft: ActionItemDraft,
  ): Promise<void> {
    await this.publish({
      type: 'CALLOUT_THREAD',
      dashboardId: anchor.dashboardId,
      reviewType: WBR_REVIEW_TYPE,
      reportingPeriod: anchor.reportingPeriod,
      metricKey: anchor.metricKey,
      metricCategory: anchor.metricCategory,
      ...draftFields(draft),
      actor: currentUserAlias(),
    });
  }

  /** Creates a dashboard-global action item. */
  public async createGlobal(
    dashboardId: string,
    draft: ActionItemDraft,
  ): Promise<void> {
    await this.publish({
      type: 'GLOBAL',
      dashboardId,
      reviewType: WBR_REVIEW_TYPE,
      ...draftFields(draft),
      actor: currentUserAlias(),
    });
  }

  /**
   * Updates an item. Emptying the message becomes an explicit clear flag;
   * absent fields keep their stored values. Assignee and eta are mandatory
   * and always sent.
   */
  public async update(
    existing: ActionItemRecord,
    draft: ActionItemDraft,
  ): Promise<void> {
    await this.publish({
      id: existing.id,
      type: existing.type,
      dashboardId: existing.dashboardId,
      // Record's own review: for GLOBAL items it is part of the partition key.
      reviewType: existing.reviewType,
      ...(existing.type === 'CALLOUT_THREAD' && existing.context
        ? { reportingPeriod: existing.context.reportingPeriod }
        : {}),
      ...draftFields(draft),
      ...(existing.message && !draft.message ? { message: undefined, clearMessage: true } : {}),
      actor: currentUserAlias(),
    });
  }

  /** Soft-deletes an item (irreversible backend-side). */
  public async remove(existing: ActionItemRecord): Promise<void> {
    await this.publish({
      id: existing.id,
      type: existing.type,
      dashboardId: existing.dashboardId,
      reviewType: existing.reviewType,
      ...(existing.type === 'CALLOUT_THREAD' && existing.context
        ? { reportingPeriod: existing.context.reportingPeriod }
        : {}),
      status: existing.status,
      isDeleted: true,
      actor: currentUserAlias(),
    });
  }
}

/** The draft's authored fields, omitting blanks so backend merge semantics apply. */
function draftFields(
  draft: ActionItemDraft,
): Pick<PublishActionItemInput, 'status'> &
  Partial<Omit<PublishActionItemInput, 'status'>> {
  return {
    heading: draft.heading,
    status: draft.status,
    assignee: draft.assignee,
    eta: draft.eta,
    ...(draft.message ? { message: draft.message } : {}),
  };
}

/** Singleton (portal convention): one shared instance app-wide. */
export const ActionItemsApi = new ActionItemsApiClient();
